// frontend/src/services/notificationService.js

class NotificationService {
  constructor() {
    // Récupérer les préférences de l'utilisateur depuis le localStorage
    this.soundEnabled = localStorage.getItem('notification_sound') !== 'disabled'
    this.volume = parseFloat(localStorage.getItem('notification_volume') || '0.5')
    this.browserNotificationsEnabled = localStorage.getItem('browser_notifications') !== 'disabled'

    // Map des sons disponibles
    this.sounds = {
      message: '/sounds/message.mp3',
      notification: '/sounds/notification.mp3',
      mention: '/sounds/mention.mp3',
    }

    // Précharger les sons pour une lecture plus rapide
    this.preloadSounds()

    // Demander la permission pour les notifications du navigateur si ce n'est pas déjà fait
    this.requestBrowserNotificationPermission()
  }

  // Précharger les sons en mémoire
  preloadSounds() {
    if (!this.soundEnabled) return

    this.audioElements = {}

    Object.entries(this.sounds).forEach(([key, path]) => {
      try {
        const audio = new Audio(path)
        audio.preload = 'auto'
        audio.volume = this.volume
        this.audioElements[key] = audio
      } catch (error) {
        console.error(`Erreur lors du préchargement du son ${key}:`, error)
      }
    })
  }

  // Jouer un son spécifique
  playSound(type = 'notification') {
    if (!this.soundEnabled) return Promise.resolve()

    try {
      // Utiliser un élément audio préchargé ou en créer un nouveau
      const audio =
        this.audioElements[type] || new Audio(this.sounds[type] || this.sounds.notification)
      audio.volume = this.volume

      return audio.play().catch((err) => {
        console.log('Impossible de jouer le son de notification:', err)
        // Probablement une restriction du navigateur, ne rien faire
        return Promise.resolve()
      })
    } catch (err) {
      console.error('Erreur lors de la lecture du son:', err)
      return Promise.resolve()
    }
  }

  // Activer/désactiver le son
  toggleSound() {
    this.soundEnabled = !this.soundEnabled
    localStorage.setItem('notification_sound', this.soundEnabled ? 'enabled' : 'disabled')

    if (this.soundEnabled) {
      this.preloadSounds()
    }

    return this.soundEnabled
  }

  // Régler le volume (entre 0 et 1)
  setVolume(volume) {
    if (volume < 0 || volume > 1) {
      throw new Error('Le volume doit être entre 0 et 1')
    }

    this.volume = volume
    localStorage.setItem('notification_volume', volume.toString())

    // Mettre à jour le volume de tous les éléments audio préchargés
    if (this.audioElements) {
      Object.values(this.audioElements).forEach((audio) => {
        audio.volume = volume
      })
    }

    return this.volume
  }

  // Vérifier si le son est activé
  isSoundEnabled() {
    return this.soundEnabled
  }

  // Obtenir le volume actuel
  getVolume() {
    return this.volume
  }

  // Demander la permission pour les notifications du navigateur
  requestBrowserNotificationPermission() {
    if (!this.browserNotificationsEnabled) return Promise.resolve(false)

    if (!('Notification' in window)) {
      console.log('Ce navigateur ne prend pas en charge les notifications.')
      return Promise.resolve(false)
    }

    if (Notification.permission === 'granted') {
      return Promise.resolve(true)
    }

    if (Notification.permission !== 'denied') {
      return Notification.requestPermission().then((permission) => {
        return permission === 'granted'
      })
    }

    return Promise.resolve(false)
  }

  // Activer/désactiver les notifications du navigateur
  toggleBrowserNotifications() {
    this.browserNotificationsEnabled = !this.browserNotificationsEnabled
    localStorage.setItem(
      'browser_notifications',
      this.browserNotificationsEnabled ? 'enabled' : 'disabled',
    )

    if (this.browserNotificationsEnabled) {
      this.requestBrowserNotificationPermission()
    }

    return this.browserNotificationsEnabled
  }

  // Vérifier si les notifications du navigateur sont activées
  areBrowserNotificationsEnabled() {
    return this.browserNotificationsEnabled && Notification.permission === 'granted'
  }

  // Afficher une notification du navigateur
  showBrowserNotification(title, options = {}) {
    if (!this.browserNotificationsEnabled || Notification.permission !== 'granted') {
      return null
    }

    try {
      // Créer et afficher la notification
      const notification = new Notification(title, {
        icon: '/logo192.png', // Assurez-vous d'avoir ce fichier dans votre dossier public
        ...options,
      })

      // Fermer automatiquement après 5 secondes
      setTimeout(() => notification.close(), 5000)

      return notification
    } catch (error) {
      console.error("Erreur lors de l'affichage de la notification du navigateur:", error)
      return null
    }
  }
}

// Singleton
const notificationService = new NotificationService()
export default notificationService
